import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Card, CardContent } from '@mui/material';
import { AccessTime } from '@mui/icons-material';

const CountdownTimer = ({ targetDate, compact = false }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  // Without this the clock renders 00:00:00:00 forever after the date passes.
  // A dead timer in the hero reads as an abandoned page.
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const target = new Date(targetDate).getTime();

    const updateCountdown = () => {
      const now = new Date().getTime();
      const distance = target - now;

      if (distance > 0) {
        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        setTimeLeft({ days, hours, minutes, seconds });
        setExpired(false);
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setExpired(true);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [targetDate]);

  if (expired) {
    return (
      <Box sx={{ mt: compact ? 0 : 3, maxWidth: 600 }}>
        <Typography sx={{ color: 'secondary.light', fontWeight: 600 }}>
          This cohort has started. The next one is forming now.
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)', mt: 0.5 }}>
          The strategy call is still free and still worth having today: month one of the plan
          runs while you're employed.
        </Typography>
      </Box>
    );
  }

  const timeUnits = [
    { label: 'Days', value: timeLeft.days, id: 'days' },
    { label: 'Hours', value: timeLeft.hours, id: 'hours' },
    { label: 'Minutes', value: timeLeft.minutes, id: 'minutes' },
    { label: 'Seconds', value: timeLeft.seconds, id: 'seconds' },
  ];

  if (compact) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        {timeUnits.map((unit) => (
          <React.Fragment key={unit.id}>
            <Typography
              component="span"
              id={unit.id}
              sx={{
                fontFamily: 'monospace',
                fontSize: '1.2rem',
                color: 'secondary.light',
                fontWeight: 600,
              }}
            >
              {unit.value.toString().padStart(2, '0')}{unit.label === 'Days' ? 'd' : unit.label === 'Hours' ? 'h' : unit.label === 'Minutes' ? 'm' : 's'}
            </Typography>
            {unit.id !== 'seconds' && (
              <Typography sx={{ color: 'secondary.light', fontSize: '1.2rem' }}>:</Typography>
            )}
          </React.Fragment>
        ))}
      </Box>
    );
  }

  return (
    <Card
      sx={{
        mt: 4,
        bgcolor: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        maxWidth: 600,
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTime sx={{ color: 'secondary.light' }} />
            <Typography sx={{ color: 'secondary.light', fontWeight: 600 }}>
              Course Starts In:
            </Typography>
          </Box>
          <Grid container spacing={2} justifyContent="center">
            {timeUnits.map((unit) => (
              <Grid key={unit.id}>
                <Box
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 2,
                    p: 2,
                    minWidth: compact ? 60 : 80,
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    id={unit.id}
                    sx={{
                      fontSize: compact ? '1.8rem' : '2.2rem',
                      fontWeight: 800,
                      color: '#c87941',
                      lineHeight: 1,
                    }}
                  >
                    {unit.value.toString().padStart(2, '0')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {unit.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CountdownTimer;